import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { put, setRequestWorkspace } from '@/lib/api';

export type ThemeMode = 'light' | 'dark';

interface UiState {
  mode: ThemeMode;
  sidebarCollapsed: boolean;
  commandPaletteOpen: boolean;
  activeWorkspaceId: string | null;
  toggleMode: () => void;
  toggleSidebar: () => void;
  setCommandPalette: (open: boolean) => void;
  setActiveWorkspace: (id: string | null) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      mode: 'light',
      sidebarCollapsed: false,
      commandPaletteOpen: false,
      activeWorkspaceId: null,

      toggleMode: () => set((s) => ({ mode: s.mode === 'light' ? 'dark' : 'light' })),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setCommandPalette: (open) => set({ commandPaletteOpen: open }),
      /**
       * Switching workspace changes what every request may see, so the header
       * is updated with the state — not by whichever screen happens to notice.
       */
      setActiveWorkspace: (id) => {
        setRequestWorkspace(id);
        set({ activeWorkspaceId: id });

        // Remembered on the account as well as in this browser, so signing in
        // on another machine opens where you left off. Best-effort: failing to
        // record a preference must not interrupt the switch itself.
        if (id) void put('/users/me/last-workspace', { workspaceId: id }).catch(() => undefined);
      },
    }),
    {
      name: 'ikomet.ui',
      /**
       * Re-arms the request header from storage on load.
       *
       * Without this the first requests after a refresh go out unscoped and
       * the app shows every workspace for a beat before settling.
       */
      onRehydrateStorage: () => (state) => setRequestWorkspace(state?.activeWorkspaceId ?? null),
      // Transient UI state stays out of storage.
      partialize: (state) => ({
        mode: state.mode,
        sidebarCollapsed: state.sidebarCollapsed,
        activeWorkspaceId: state.activeWorkspaceId,
      }),
    },
  ),
);
